import {
  Alert,
  AlertVariants,
  Box,
  ButtonGroup,
  Dialog,
  Divider,
  FormControlSizes,
  FormGroup,
  HelpText,
  HStack,
  Icon,
  ICON_SIZES,
  IconName,
  Input,
  type MapTo,
  NotificationVariants,
  runValidation,
  SearchSelect,
  Text,
  TextArea,
  Toggle,
  useCallbackRef,
  useGlobalToasts,
  VStack,
} from '@talos/kyoko';
import { identity } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { type BaseSchema, object, string } from 'yup';
import { useFeatureFlag } from '../../hooks';
import { envConfig } from '../../utils/envConfig';
import { Link } from '../OMS/styles';
import type { CreateSupportTicketResponse } from './types';
import {
  availableRequestTypes,
  getAvailablePriorities,
  type SupportFormArg,
  type SupportTicketDialogProps,
  type SupportTicketForm,
} from './types';
import { useSupportTicketDialog } from './useSupportTicketDialog';

const schema: Partial<Record<keyof SupportTicketForm, BaseSchema>> = {
  summary: string().required('Summary is required'),
  priority: string().required('Urgency is required'),
  requestType: string().required('Type is required'),
  description: string().required('Description is required'),
};

const initialForm: SupportTicketForm = {
  summary: '',
  requestType: undefined,
  description: '',
  priority: undefined,
  pagerDutyAlert: false,
};

export function SupportTicketDialog(props: SupportTicketDialogProps) {
  const { dialog } = props;
  const { isOpen } = dialog;
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<CreateSupportTicketResponse | undefined>(undefined);
  const [form, setForm] = useState<SupportTicketForm>(initialForm);
  const { createTicket } = useSupportTicketDialog();
  const [errors, setErrors] = useState<Partial<SupportTicketForm>>(validate(form));
  const [touched, setTouched] = useState<Partial<MapTo<SupportTicketForm, boolean>>>({});
  const { add: addToast } = useGlobalToasts();

  const isValid = useMemo(() => Object.keys(errors).length === 0, [errors]);
  const isDirty = useMemo((): boolean => {
    return form.summary !== '' || form.description !== '';
  }, [form]);
  const { enableSupportModalPagerDuty } = useFeatureFlag();

  const availablePriorities = useMemo(() => getAvailablePriorities(form.requestType), [form.requestType]);

  const helpText = useMemo(() => {
    switch (form.priority) {
      case 'Critical':
        return "The platform is unusable or a key feature is completely down. Examples: You can't trade, order has incorrect economics.";
      case 'High':
        return "A major feature isn't working properly and is seriously affecting your workflows. Examples: Delays or errors in order execution or reporting.";
      case 'Medium':
        return "A feature isn't working as expected, but does not have immediate business impact. Examples: Issues with UI filters, or issue setting up a new credential.";
      case 'Low':
        return "Something looks off or minor functionality isn't behaving right, but not affecting your ability to use the platform. Examples: Cosmetic issues, display glitches, or typos.";
      case undefined:
        return '';
      default: {
        const _exhaustiveCheck: never = form.priority;
      }
    }
  }, [form.priority]);

  const updateForm = useCallbackRef(({ key, value }: SupportFormArg) => {
    const newForm = { ...form, [key]: value };
    setTouched(prev => ({ ...prev, [key]: true }));
    const newAvailablePriorities = getAvailablePriorities(newForm.requestType);
    if (!newAvailablePriorities.includes(newForm.priority)) {
      newForm.priority = undefined;
    }
    if (newForm.priority === 'Critical') {
      newForm.requestType = 'Trading or System Issue';
    }
    setForm(newForm);
    setErrors(validate(newForm));
  });

  const setAllTouched = useCallback(() => {
    setTouched({
      summary: true,
      priority: true,
      requestType: true,
      description: true,
    });
  }, []);

  const handleConfirmMouseover = useCallback(() => {
    setAllTouched();
    setErrors(validate(form));
  }, [setAllTouched, form]);

  const handleSubmit = useCallback(async () => {
    setLoading(true);
    createTicket(form)
      .then((response: CreateSupportTicketResponse) => {
        setResponse(response);
      })
      .catch((e: ErrorEvent) => {
        addToast({
          variant: NotificationVariants.Negative,
          text: `Unable to create Support Ticket: ${e.message}`,
        });
      })
      .finally(() => setLoading(false));
  }, [createTicket, form, addToast]);

  // Reset form on dialog open
  useEffect(() => {
    if (isOpen) {
      setForm(initialForm);
      setErrors({});
      setResponse(undefined);
      setTouched({});
    }
  }, [isOpen]);

  return (
    <Dialog
      headerIcon={IconName.Ticket}
      title="Support Ticket"
      {...dialog}
      confirmLabel="Send Support Ticket"
      confirmDisabled={!isDirty || !isValid || loading}
      confirmLoading={loading}
      cancelLabel={response ? 'Close' : 'Cancel'}
      onConfirm={handleSubmit}
      onConfirmMouseOver={handleConfirmMouseover}
      closeOnConfirm={false}
      showClose
      showConfirm={!response}
      stretchButtons
      width={600}
    >
      {!response && (
        <>
          <FormGroup label="Summary" controlId="ticket-summary" error={touched.summary && errors.summary}>
            <Input
              id="ticket-summary"
              value={form.summary}
              onChange={e => updateForm({ key: 'summary', value: e.target.value })}
              inputType="text"
              data-testid="ticket-summary"
              disabled={loading}
              invalid={Boolean(touched.summary && errors.summary)}
            />
          </FormGroup>

          <FormGroup label="Urgency" flex="1" error={touched.priority && errors.priority}>
            <SearchSelect
              options={availablePriorities}
              getLabel={identity}
              initialSortByLabel={false}
              showDropdownSearch={false}
              selection={form.priority}
              onChange={value => updateForm({ key: 'priority', value })}
              data-testid="ticket-priority"
              disabled={loading}
              showClear
              invalid={Boolean(touched.priority && errors.priority)}
              w="100%"
            />
            {helpText && (
              <Box mt="spacing2">
                <HelpText>{helpText}</HelpText>
              </Box>
            )}
          </FormGroup>

          <FormGroup label="Type" flex="1" error={touched.requestType && errors.requestType}>
            <SearchSelect
              options={availableRequestTypes}
              getLabel={identity}
              initialSortByLabel={false}
              showDropdownSearch={false}
              selection={form.requestType}
              onChange={value => updateForm({ key: 'requestType', value: value || '' })}
              data-testid="ticket-type"
              disabled={loading}
              invalid={Boolean(touched.requestType && errors.requestType)}
              showClear
            />
          </FormGroup>

          <FormGroup label="Description" error={touched.description && errors.description}>
            <TextArea
              rows={3}
              value={form.description}
              onChange={e => updateForm({ key: 'description', value: e.target.value })}
              style={{ textAlign: 'left' }}
              data-testid="ticket-description"
              disabled={loading}
              invalid={Boolean(touched.description && errors.description)}
            />
          </FormGroup>

          <HStack mb="spacing16" gap="spacing4" alignItems="center" justifyContent="flext-start" textAlign="left">
            <Icon icon={IconName.InformationCircle} />
            <Text>In case of urgent issues, please contact Talos Support on {envConfig.VITE_SUPPORT_PHONE}</Text>
          </HStack>

          {enableSupportModalPagerDuty && form.priority === 'Critical' && (
            <>
              <Divider />
              <FormGroup
                mt="spacing8"
                mb="0"
                inline
                label="Escalate"
                alignItems="center"
                tooltip="Escalate your issue to on call support"
              >
                <ButtonGroup justifyContent="flex-end" flex="1">
                  <Toggle
                    data-testid="ticket-pagerduty-toggle"
                    size={FormControlSizes.Default}
                    checked={form.pagerDutyAlert}
                    onChange={(val: boolean) =>
                      updateForm({
                        key: 'pagerDutyAlert',
                        value: val,
                      })
                    }
                  />
                </ButtonGroup>
              </FormGroup>
              {form.pagerDutyAlert && (
                <VStack gap="spacing8">
                  <Alert mt="spacing8" dismissable={false} variant={AlertVariants.Warning}>
                    You are about to page a member of the support staff for a matter of emergency.
                  </Alert>
                </VStack>
              )}
            </>
          )}
        </>
      )}
      {response && (
        <>
          {form.pagerDutyAlert && (
            <Alert mb="spacing24" dismissable={false} variant={AlertVariants.Warning}>
              A member of the support staff has been paged.
            </Alert>
          )}
          <VStack gap="spacing16" justifyContent="center" h="100%">
            <Icon icon={IconName.CheckCircle} color="colors.green.lighten" size={ICON_SIZES.MEDIUM * 2} />
            <Text fontSize="fontSizeHuge" weight="500" color="colorTextImportant">
              Support Ticket Sent
            </Text>
            <Text size="fontSizeLarge">
              We have received your support ticket and will get back to you as soon as possible.
            </Text>
            <Link href={response.supportLink} target="_blank">
              {response.supportLink}
            </Link>
          </VStack>
        </>
      )}
    </Dialog>
  );
}

const validate = function (form: Partial<SupportTicketForm>) {
  return runValidation(object().shape(schema), form);
};
